import { useState, useEffect, useRef, useCallback } from 'react';
import { Card } from '@consta/uikit/Card';
import { Text } from '@consta/uikit/Text';
import { TextField } from '@consta/uikit/TextField';
import { Button } from '@consta/uikit/Button';
import { Combobox } from '@consta/uikit/Combobox';
import { Modal } from '@consta/uikit/Modal';
import { IconAreaChart } from '@consta/icons/IconAreaChart';
import { IconGas } from '@consta/icons/IconGas';
import { IconTrash } from '@consta/icons/IconTrash';
import { IconSave } from '@consta/icons/IconSave';
import { IconAdd } from '@consta/icons/IconAdd';
import { IconEdit } from '@consta/icons/IconEdit';
import { IconDrag } from '@consta/icons/IconDrag';
import { IconClose } from '@consta/icons/IconClose';
import * as echarts from 'echarts';

import classes from './style/ProjectResults.module.scss';
import { Project } from '../../../../../services/types/TProjectServices';
import {
    useLazyGetChartByWellQuery,
    useLazyGetChartUuidQuery,
    useUpdateChartMutation
} from '../../../../../services/chartService';
import {
    useLazyGetWellUuidQuery,
    useLazyGetWellByProjectQuery,
    usePatchWellMutation,
    useExportWellResultsReportMutation,
    useLazyGetWellInterpolationMapsQuery,
    useLazyGetWellCommentsQuery,
    useLazyGetWellQuery,
    useLazyGetReportWellInfoQuery
} from '../../../../../services/wellService';
import { usePatchInterpolationMapMutation } from '../../../../../services/interpolationMapService';
import { usePostInterpolationMapMutation } from '../../../../../services/commentService';
import AddGraphic from '../../../../Modals/AddGraphic/AddGraphic';
import AddMap from '../../../../Modals/AddMap/AddMap';
import {
    useDeleteResultTableMutation,
    useLazyGetResultTableQuery,
    usePatchResultTableMutation,
    usePostResultTableMutation
} from '../../../../../services/resultTableService';
import { fieldFiltersPresent } from '@consta/uikit/__internal__/src/components/Table/filtering';
import { useLazyGetFieldQuery } from '../../../../../services/fieldService';
import { useLocation } from 'react-router-dom';

interface ProjectResultsProps {
    userAccessLevel: number | null;
    projectData?: Project;
    projectUuid?: string;
}

interface ChartDataPoint {
    value: [number, number];
    itemStyle: {
        color: string;
    };
    label: {
        show: boolean;
        formatter: number | string;
    };
}

interface ChartData {
    uuid: string;
    name: string;
    comment: string;
    build_by_parameter: string;
    data: ChartDataPoint[];
}

interface WellItem {
    id: string;
    label: string;
    uuid: string;
}

interface InterpolationMap {
    id: number;
    uuid: string;
    name: string;
    image: string;
    comment: string;
    well: string;
}

interface ResultTable {
    id: number;
    uuid: string;
    name: string;
    content: {
        headers: string[];
        body: string[][];
    };
    created_at: string;
    updated_at: string;
    well: string;
}

interface UserInfo {
    id: number;
    uuid: string;
    first_name: string;
    last_name: string;
    middle_name: string | null;
    username: string;
    email: string;
    company: {
        uuid: string;
        name: string;
    };
    role: {
        name: string;
        access_level: number;
    };
}

interface Comment {
    id: number;
    user_fio: string;
    child: any;
    uuid: string;
    created_at: string;
    updated_at: string;
    body: string;
    project: string;
    well: string;
    author: string;
    parent: string | null;
}

// Вспомогательные функции для работы с таблицами
const TableUtils = {
    normalizeTableData: (table: any): ResultTable => {
        try {
            let content;
            
            if (typeof table.content === 'string') {
                content = JSON.parse(table.content);
            } else if (table.content && typeof table.content === 'object') {
                content = table.content;
            } else {
                content = { headers: [], body: [] };
            }

            return {
                ...table,
                content: {
                    headers: Array.isArray(content.headers) ? content.headers : [],
                    body: Array.isArray(content.body) ? content.body : []
                }
            };
        } catch (error) {
            console.error('Ошибка нормализации данных таблицы:', error);
            return {
                ...table,
                content: {
                    headers: ['Параметр', 'Значение'],
                    body: [['', '']]
                }
            };
        }
    },

    createDefaultTableData: (): { headers: string[]; body: string[][] } => ({
        headers: [],
        body: []
    })
};

// Компонент редактируемой ячейки
const EditableCell = ({
    value,
    onChange,
    placeholder = '',
    disabled = false
}: {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    disabled?: boolean;
}) => {
    const [localValue, setLocalValue] = useState(value);

    useEffect(() => {
        setLocalValue(value);
    }, [value]);

    const handleChange: any = (newValue: string) => {
        setLocalValue(newValue);
    };

    const handleBlur = () => {
        if (localValue !== value) {
            onChange(localValue);
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleBlur();
        }
    };

    return (
        <TextField
            value={localValue}
            onChange={handleChange}
            onBlur={handleBlur}
            onKeyPress={handleKeyPress}
            size="s"
            placeholder={placeholder}
            disabled={disabled}
            style={{ width: '100%', minWidth: '120px', border: '0px !important' }}
        />
    );
};

// Компонент таблицы
const CustomTable = ({
    table,
    isEditing,
    onTableUpdate,
    onTableDelete,
    userAccessLevel
}: {
    table: ResultTable;
    isEditing: boolean;
    onTableUpdate: (table: ResultTable) => void;
    onTableDelete: (tableUuid: string) => void;
    userAccessLevel: number | null;
}) => {
    const [tableName, setTableName] = useState<any>(table.name || 'Новая таблица');
    const [isSaving, setIsSaving] = useState(false);
    const [draggedColumn, setDraggedColumn] = useState<number | null>(null);

    const normalizedTable = TableUtils.normalizeTableData(table);
    const { headers, body } = normalizedTable.content;

    // Обработчики для заголовков
    const handleHeaderChange = (index: number, value: string) => {
        const newHeaders = [...headers];
        newHeaders[index] = value;
        
        onTableUpdate({
            ...table,
            content: {
                headers: newHeaders,
                body
            }
        });
    };

    const handleAddColumn = () => {
        const newHeaders = [...headers, `Столбец ${headers.length + 1}`];
        const newBody = body.map(row => [...row, '']);
        
        onTableUpdate({
            ...table,
            content: {
                headers: newHeaders,
                body: newBody
            }
        });
    };

    const handleRemoveColumn = (index: number) => {
        if (headers.length <= 1) {
            alert('Нельзя удалить последний столбец');
            return;
        }

        const newHeaders = headers.filter((_, i) => i !== index);
        const newBody = body.map(row => row.filter((_, i) => i !== index));
        
        onTableUpdate({
            ...table,
            content: {
                headers: newHeaders,
                body: newBody
            }
        });
    };

    // Обработчики для строк
    const handleCellChange = (rowIndex: number, colIndex: number, value: string) => {
        const newBody = body.map((row, rIndex) => 
            rIndex === rowIndex 
                ? row.map((cell, cIndex) => cIndex === colIndex ? value : cell)
                : row
        );
        
        onTableUpdate({
            ...table,
            content: {
                headers,
                body: newBody
            }
        });
    };

    const handleAddRow = () => {
        const newRow = Array(headers.length).fill('');
        const newBody = [...body, newRow];
        
        onTableUpdate({
            ...table,
            content: {
                headers,
                body: newBody
            }
        });
    };

    const handleRemoveRow = (index: number) => {
        if (body.length <= 1) {
            alert('Нельзя удалить последнюю строку');
            return;
        }

        const newBody = body.filter((_, i) => i !== index);
        
        onTableUpdate({
            ...table,
            content: {
                headers,
                body: newBody
            }
        });
    };

    // Drag & Drop для столбцов
    const handleDragStart = (index: number) => {
        setDraggedColumn(index);
    };

    const handleDragOver = (e: React.DragEvent, index: number) => {
        e.preventDefault();
    };

    const handleDrop = (e: React.DragEvent, targetIndex: number) => {
        e.preventDefault();
        if (draggedColumn === null || draggedColumn === targetIndex) return;

        const newHeaders = [...headers];
        const newBody = body.map(row => [...row]);

        // Перемещаем заголовок
        const [movedHeader] = newHeaders.splice(draggedColumn, 1);
        newHeaders.splice(targetIndex, 0, movedHeader);

        // Перемещаем данные в каждой строке
        newBody.forEach(row => {
            const [movedCell] = row.splice(draggedColumn, 1);
            row.splice(targetIndex, 0, movedCell);
        });

        onTableUpdate({
            ...table,
            content: {
                headers: newHeaders,
                body: newBody
            }
        });

        setDraggedColumn(null);
    };

    // Сохранение таблицы
    const handleSave = async () => {
        if (!userAccessLevel || userAccessLevel === 0) return;

        setIsSaving(true);
        try {
            await onTableUpdate({
                ...table,
                name: tableName
            });
        } catch (error) {
            console.error('Ошибка сохранения таблицы:', error);
            alert('Ошибка при сохранении таблицы');
        } finally {
            setIsSaving(false);
        }
    };

    const canEdit = userAccessLevel !== null && userAccessLevel !== 0;

    return (
        <Card className={classes.tableCard} verticalSpace="m" horizontalSpace="m">
            {/* Заголовок таблицы и кнопки управления */}
            <div className={classes.tableHeader}>
                <div className={classes.tableTitle}>
                    {isEditing ? (
                        <TextField
                            value={tableName}
                            onChange={setTableName}
                            placeholder="Название таблицы"
                            size="s"
                            style={{ minWidth: '300px' }}
                            onBlur={handleSave}
                        />
                    ) : (
                        <Text size="xl" weight="bold">
                            {table.name || 'Новая таблица'}
                        </Text>
                    )}
                    <Text size="s" view="secondary">
                        Обновлено: {new Date(table.updated_at || Date.now()).toLocaleDateString()}
                    </Text>
                </div>

                {canEdit && (
                    <div className={classes.tableActions}>
                        {isEditing && (
                            <>
                                <Button
                                    size="s"
                                    label="Добавить строку"
                                    onClick={handleAddRow}
                                    iconLeft={IconAdd}
                                />
                                <Button
                                    size="s"
                                    label="Добавить столбец"
                                    onClick={handleAddColumn}
                                    iconLeft={IconAdd}
                                />
                                {/* <Button
                                    size="s"
                                    label={isSaving ? "Сохранение..." : "Сохранить"}
                                    onClick={handleSave}
                                    iconLeft={IconSave}
                                    loading={isSaving}
                                /> */}
                            </>
                        )}
                        <Button
                            size="s"
                            label="Удалить таблицу"
                            onClick={() => {
                                if (window.confirm('Вы уверены, что хотите удалить эту таблицу?')) {
                                    onTableDelete(table.uuid);
                                }
                            }}
                            iconLeft={IconTrash}
                        />
                    </div>
                )}
            </div>

            {/* Таблица */}
            <div className={classes.customTable}>
                {/* Заголовки таблицы */}
                <div className={classes.tableHeaders}>
                    {headers.map((header, index) => (
                        <div
                            key={index}
                            className={classes.tableHeaderCell}
                            draggable={isEditing}
                            onDragStart={() => handleDragStart(index)}
                            onDragOver={(e) => handleDragOver(e, index)}
                            onDrop={(e) => handleDrop(e, index)}
                        >
                            <div className={classes.headerContent}>
                                {isEditing && (
                                    <Button
                                        size="xs"
                                        view="clear"
                                        onlyIcon
                                        iconLeft={IconDrag}
                                        className={classes.dragHandle}
                                        title="Перетащите для изменения порядка"
                                    />
                                )}
                                
                                {isEditing ? (
                                    <EditableCell
                                        value={header}
                                        onChange={(value) => handleHeaderChange(index, value)}
                                        placeholder="Название столбца"
                                    />
                                ) : (
                                    <Text weight="semibold" className={classes.headerText}>
                                        {header}
                                    </Text>
                                )}

                                {isEditing && headers.length > 1 && (
                                    <Button
                                        size="xs"
                                        view="clear"
                                        onlyIcon
                                        iconLeft={IconClose}
                                        onClick={() => handleRemoveColumn(index)}
                                        title="Удалить столбец"
                                        className={classes.removeButton}
                                    />
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Тело таблицы */}
                <div className={classes.tableBody}>
                    {body.map((row, rowIndex) => (
                        <div key={rowIndex} className={classes.tableRow}>
                            {row.map((cell, colIndex) => (
                                <div key={colIndex} className={classes.tableCell}>
                                    <EditableCell
                                        value={cell}
                                        onChange={(value) => handleCellChange(rowIndex, colIndex, value)}
                                        placeholder="Введите значение"
                                        disabled={!isEditing}
                                    />
                                </div>
                            ))}
                            
                            {isEditing && (
                                <div className={classes.rowActions}>
                                    {body.length > 1 && (
                                        <Button
                                            size="xs"
                                            view="clear"
                                            onlyIcon
                                            iconLeft={IconTrash}
                                            onClick={() => handleRemoveRow(rowIndex)}
                                            title="Удалить строку"
                                        />
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {/* Сообщение если таблица пустая */}
                {headers.length === 0 && (
                    <div className={classes.emptyTable}>
                        <Text view="secondary">Таблица пуста</Text>
                        {isEditing && (
                            <Button
                                size="s"
                                label="Добавить первый столбец"
                                onClick={handleAddColumn}
                                iconLeft={IconAdd}
                            />
                        )}
                    </div>
                )}
            </div>
        </Card>
    );
};

// Компонент секции графиков
const GraphicsSection = ({ userAccessLevel, chartsData, isLoading, error, onEditChart }: any) => {
    const chartRefs = useRef<(HTMLDivElement | null)[]>([]);
    const chartInstances = useRef<(echarts.ECharts | null)[]>([]);

    const createLegendData = (chartData: ChartData) => {
        const uniqueColors = new Set();
        const legendData: { name: string; itemStyle: { color: string } }[] = [];

        chartData.data.forEach(point => {
            const color = point.itemStyle.color;
            if (!uniqueColors.has(color)) {
                uniqueColors.add(color);
                const wellCount = chartData.data.filter(p => p.itemStyle.color === color).length;
                legendData.push({
                    name: `${wellCount} скважин`,
                    itemStyle: { color }
                });
            }
        });

        return legendData;
    };

   useEffect(() => {
    if (chartsData && Array.isArray(chartsData)) {
        chartsData.forEach((chart: any, index: number) => {
            const chartRef = chartRefs.current[index];
            if (chartRef && chart.data && Array.isArray(chart.data)) {
                // Удаляем предыдущий экземпляр графика
                if (chartInstances.current[index]) {
                    chartInstances.current[index]?.dispose();
                }

                // Создаем новый экземпляр графика
                const chartInstance = echarts.init(chartRef);
                chartInstances.current[index] = chartInstance;

                // Группируем точки по цветам и создаем серии
                const colorGroups = new Map();

                chart.data.forEach((point: any) => {
                    const color = point.itemStyle?.color;
                    if (color) {
                        if (!colorGroups.has(color)) {
                            colorGroups.set(color, []);
                        }
                        // Создаем данные с явным указанием значения для подписи
                        colorGroups.get(color).push([
                            point.value[0], // x значение
                            point.value[1], // y значение (порядковый номер)
                            point.label?.formatter?.toString() || '', // номер скважины для подписи
                            point.value[0]?.toFixed(4) // значение для tooltip
                        ]);
                    }
                });

                // Создаем серии для каждого цвета
                const series = Array.from(colorGroups.entries()).map(([color, points]) => {
                    // Находим название для этого цвета в легенде
                    const legendItem = chart.legend?.find((item: any) => item.color === color);
                    const seriesName = legendItem?.raw_color || `Серия ${color}`;

                    return {
                        type: 'scatter' as const,
                        name: seriesName,
                        data: points,
                        symbolSize: 8,
                        itemStyle: {
                            color: color
                        },
                        label: {
                            show: true,
                            position: 'right',
                            formatter: (params: any) => {
                                // Берем третье значение из массива данных - номер скважины из label.formatter
                                return params.data[2] || '';
                            },
                            fontSize: 10,
                            color: '#333'
                        },
                        emphasis: {
                            focus: 'series',
                            scale: true
                        }
                    };
                });

                // Создаем данные для легенды
                const legendData = (chart.legend || []).map((item: any) => item.raw_color);

                const option = {
                    title: {
                        text: chart.name || 'График',
                        left: 'center',
                        textStyle: {
                            fontSize: 14
                        }
                    },
                    tooltip: {
                        trigger: 'item',
                        formatter: (params: any) => {
                            const data = params.data;
                            if (!data) return '';
                            return `Скважина: ${data[2] || 'N/A'}<br/>Значение: ${data[3] || '0'}<br/>Порядковый номер: ${data[1] || '0'}`;
                        }
                    },
                    legend: {
                        data: legendData,
                        top: 'bottom',
                        left: 'center',
                        itemWidth: 12,
                        itemHeight: 12,
                        textStyle: {
                            fontSize: 10
                        }
                    },
                    xAxis: {
                        type: 'value',
                        name: 'Значение',
                        nameLocation: 'middle',
                        nameGap: 25,
                        nameTextStyle: {
                            fontSize: 12
                        },
                        position: 'top' // Ось X сверху
                    },
                    yAxis: {
                        type: 'value',
                        name: 'Порядковый номер',
                        nameLocation: 'middle',
                        nameGap: 25,
                        nameTextStyle: {
                            fontSize: 12
                        },
                        inverse: true // Реверс оси Y
                    },
                    series: series,
                    grid: {
                        left: '15%',
                        right: '20%',
                        bottom: '20%',
                        top: '15%',
                        containLabel: true
                    }
                };

                chartInstance.setOption(option);
            }
        });

        const handleResize = () => {
            chartInstances.current.forEach(instance => {
                instance?.resize();
            });
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            chartInstances.current.forEach(instance => {
                instance?.dispose();
            });
            chartInstances.current = [];
        };
    }
}, [chartsData]);

    const addChartRef = (index: number) => (el: HTMLDivElement | null) => {
        chartRefs.current[index] = el;
    };

    if (isLoading) {
        return (
            <div className={classes.graphic}>
                <Text>Загрузка графиков...</Text>
            </div>
        );
    }

    if (error) {
        console.error('Ошибка загрузки графиков:', error);
        return (
            <div className={classes.graphic}>
                <Text view="warning">Ошибка при загрузке графиков</Text>
            </div>
        );
    }

    if (!chartsData || (Array.isArray(chartsData) && chartsData.length === 0)) {
        return null;
    }

    const chartsArray = Array.isArray(chartsData) ? chartsData : [chartsData];
    const rows = [];
    for (let i = 0; i < chartsArray.length; i += 2) {
        rows.push(chartsArray.slice(i, i + 2));
    }

    return (
        <div className={classes.graphic}>
            <Text weight='bold' size='xl'>Графики</Text>
            {rows.map((row, rowIndex) => (
                <div key={rowIndex} className={classes.graphicRow}>
                    {/*@ts-ignore */}
                    {row.map((chart: ChartData, indexInRow: number) => {
                        const globalIndex = rowIndex * 2 + indexInRow;
                        return (
                            <div key={chart.uuid} className={classes.graphicItem}>
                                <Card className={classes.card}>
                                    <div
                                        ref={addChartRef(globalIndex)}
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            minHeight: '300px'
                                        }}
                                    />
                                </Card>
                                <div className={classes.cardInfo}>
                                    <Text className={classes.chartTitle}>{chart.name}</Text>
                                    <Text size="s" view="secondary" className={classes.chartParameter}>
                                        Параметр: {chart.build_by_parameter}
                                    </Text>
                                    {chart.comment && (
                                        <Text size="s" view="secondary" className={classes.chartComment}>
                                            {chart.comment}
                                        </Text>
                                    )}
                                    {userAccessLevel !== 0 && (
                                        <Button
                                            label='Редактировать'
                                            size='xs'
                                            onClick={() => onEditChart(chart.uuid)}
                                        />
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            ))}
        </div>
    );
};

// Компонент секции карт интерполяции
const InterpolationMapsSection = ({
    userAccessLevel,
    interpolationMapsData,
    isLoading,
    onEditMap
}: any) => {
    if (isLoading) {
        return (
            <div className={classes.interpolationMaps}>
                <Text>Загрузка карт интерполяции...</Text>
            </div>
        );
    }

    if (!interpolationMapsData) {
        return null;
    }

    const maps: InterpolationMap[] = interpolationMapsData.results || (Array.isArray(interpolationMapsData) ? interpolationMapsData : []);

    if (maps.length === 0) {
        return null;
    }

    const rows = [];
    for (let i = 0; i < maps.length; i += 2) {
        rows.push(maps.slice(i, i + 2));
    }

    return (
        <div className={classes.interpolationMaps}>
            <Text weight='bold' size='xl'>Карты интерполяции</Text>
            <div className={classes.mapsGrid}>
                {rows.map((row, rowIndex) => (
                    <div key={rowIndex} className={classes.mapsRow}>
                        {/*@ts-ignore */}
                        {row.map((map: InterpolationMap) => (
                            <div key={map.uuid} className={classes.mapItem}>
                                <Card className={classes.card}>
                                    {map.image ? (
                                        <img
                                            src={map.image}
                                            alt={map.name}
                                            style={{ width: '100%', height: 'auto', maxHeight: '300px', objectFit: 'contain' }}
                                        />
                                    ) : (
                                        <Text size="s" view="secondary">Изображение недоступно</Text>
                                    )}
                                </Card>
                                <div className={classes.cardInfo}>
                                    <Text className={classes.chartTitle}>{map.name}</Text>
                                    {map.comment && (
                                        <Text size="s" view="secondary" className={classes.chartComment}>
                                            {map.comment}
                                        </Text>
                                    )}
                                    {userAccessLevel !== 0 && (
                                        <Button
                                            label='Редактировать'
                                            size='xs'
                                            onClick={() => onEditMap(map)}
                                        />
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        </div>
    );
};

// Компонент секции заключения
const ConclusionSection = ({
    userAccessLevel,
    wellData,
    isLoadingWell,
    onEditConclusion,
    onExportReport,
    isExporting
}: {
    userAccessLevel: number | null;
    wellData: any;
    isLoadingWell: boolean;
    onEditConclusion: () => void;
    onExportReport: () => void;
    isExporting: boolean;
}) => {
    const canEdit = userAccessLevel !== null && userAccessLevel !== 0;
    const hasConclusion = wellData?.result && wellData.result.trim() !== '';

    if (isLoadingWell) {
        return (
            <div className={classes.end}>
                <Text size='xl' weight='bold' style={{ color: '#0078d2' }}>Заключение</Text>
                <Card className={classes.endCard}>
                    <Text size='s'>Загрузка данных...</Text>
                </Card>
            </div>
        );
    }

    return (
        <div className={classes.end}>
            <Text size='xl' weight='bold' style={{ color: '#0078d2' }}>Заключение</Text>
            
            {hasConclusion ? (
                // Показываем существующее заключение
                <>
                    <Card className={classes.endCard}>
                        <Text size='s'>{wellData.result}</Text>
                    </Card>
                    <div className={classes.conclusionActions}>
                        {canEdit && (
                            <Button
                                label='Редактировать заключение'
                                onClick={onEditConclusion}
                                iconLeft={IconEdit}
                            />
                        )}
                        <Button
                            label={isExporting ? "Экспорт..." : "Скачать отчет"}
                            onClick={onExportReport}
                            loading={isExporting}
                            disabled={isExporting}
                        />
                    </div>
                </>
            ) : (
                // Показываем состояние "нет заключения"
                <>
                    <Card className={classes.endCard}>
                        <div className={classes.noConclusion}>
                            <Text size='s' view='secondary' align='center'>
                                Заключение еще не написано
                            </Text>
                            {canEdit && (
                                <Text size='s' view='secondary' align='center'>
                                    Нажмите "Написать заключение" чтобы добавить текст заключения
                                </Text>
                            )}
                        </div>
                    </Card>
                    <div className={classes.conclusionActions}>
                        {canEdit && (
                            <Button
                                label='Написать заключение'
                                onClick={onEditConclusion}
                                iconLeft={IconAdd}
                            />
                        )}
                        <Button
                            label={isExporting ? "Экспорт..." : "Скачать отчет"}
                            onClick={onExportReport}
                            loading={isExporting}
                            disabled={isExporting || !hasConclusion}
                            view={hasConclusion ? "primary" : "secondary"}
                        />
                    </div>
                </>
            )}
        </div>
    );
};

// Компонент секции комментариев для скважин
const CommentsSection = ({ wellUuid }: { wellUuid?: string }) => {
  const [comments, setComments] = useState<Comment[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newComment, setNewComment] = useState<any>('');
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyText, setReplyText] = useState<any>('');
  
  // Lazy запрос комментариев скважины
  const [getComments] = useLazyGetWellCommentsQuery();
  
  // Мутация для создания комментария
  const [createComment] = usePostInterpolationMapMutation();

  // Функция для получения информации о пользователе из localStorage
  const getUserInfo = (): UserInfo | null => {
    try {
      const userInfo = localStorage.getItem('userInfo');
      if (userInfo) {
        return JSON.parse(userInfo);
      }
    } catch (error) {
      console.error('Error parsing userInfo from localStorage:', error);
    }
    return null;
  };

  // Функция для получения полного имени пользователя
  const getUserFullName = (): string => {
    const userInfo = getUserInfo();
    if (userInfo) {
      const { first_name, last_name, middle_name } = userInfo;
      return `${last_name} ${first_name}${middle_name ? ` ${middle_name}` : ''}`.trim();
    }
    return 'Анонимный пользователь';
  };

  // Функция для получения UUID пользователя
  const getUserUuid = (): string | null => {
    const userInfo = getUserInfo();
    return userInfo?.uuid || null;
  };

  // Функция для обновления списка комментариев
  const updateComments = async () => {
    if (!wellUuid) return;
    
    try {
      const response = await getComments({ well_uuid: wellUuid }).unwrap();
      // Преобразуем ответ в массив комментариев
      if (Array.isArray(response)) {
        setComments(response);
      } else {
        // Если ответ - одиночный комментарий, оборачиваем в массив
        setComments([response]);
      }
    } catch (err) {
      console.error('Error fetching comments:', err);
      setError('Ошибка при загрузке комментариев');
    }
  };

  const fetchComments = async () => {
    if (!wellUuid) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await getComments({ well_uuid: wellUuid }).unwrap();
      // Преобразуем ответ в массив комментариев
      if (Array.isArray(response)) {
        setComments(response);
      } else {
        // Если ответ - одиночный комментарий, оборачиваем в массив
        setComments([response]);
      }
    } catch (err) {
      setError('Ошибка при загрузке комментариев');
      console.error('Error fetching comments:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchComments();
  }, [wellUuid]);

  // ФУНКЦИЯ ОТПРАВКИ КОММЕНТАРИЯ
  const handleAddComment = () => {
    const commentText = newComment?.trim();
    if (!commentText || !wellUuid) return;

    const userUuid = getUserUuid();
    if (!userUuid) {
      setError('Не удалось определить пользователя');
      return;
    }

    const commentData = {
      body: commentText,
      well: wellUuid, // ЗДЕСЬ МЕНЯЕМ project НА well
      author: userUuid
    };

    // Отправляем комментарий и обновляем список
    createComment(commentData).unwrap()
      .then(() => {
        // Очищаем поле ввода
        setNewComment('');
        // ОБНОВЛЯЕМ список комментариев
        updateComments();
      })
      .catch((err) => {
        console.error('Error creating comment:', err);
        setError('Ошибка при создании комментария');
      });
  };

  // ФУНКЦИЯ ОТПРАВКИ ОТВЕТА
  const handleReply = (parentCommentUuid: string) => {
    const replyTextValue = replyText?.trim();
    if (!replyTextValue || !wellUuid) return;

    const userUuid = getUserUuid();
    if (!userUuid) {
      setError('Не удалось определить пользователя');
      return;
    }

    const replyData = {
      body: replyTextValue,
      well: wellUuid, // ЗДЕСЬ МЕНЯЕМ project НА well
      author: userUuid,
      parent: parentCommentUuid
    };

    // Отправляем ответ и обновляем список
    createComment(replyData).unwrap()
      .then(() => {
        // Очищаем поле ответа и закрываем форму
        setReplyText('');
        setReplyingTo(null);
        // ОБНОВЛЯЕМ список комментариев
        updateComments();
      })
      .catch((err) => {
        console.error('Error creating reply:', err);
        setError('Ошибка при создании ответа');
      });
  };

  const handleStartReply = (commentUuid: string) => {
    setReplyingTo(commentUuid);
    setReplyText('');
  };

  const handleCancelReply = () => {
    setReplyingTo(null);
    setReplyText('');
  };

  const formatCommentDate = (dateString: string) => {
    if (!dateString) return 'Дата не указана';
    try {
      return new Date(dateString).toLocaleDateString('ru-RU');
    } catch {
      return 'Неверная дата';
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAddComment();
    }
  };

  const handleReplyKeyPress = (e: React.KeyboardEvent, parentCommentUuid: string) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleReply(parentCommentUuid);
    }
  };

  // УПРОЩЕННАЯ ПРОВЕРКА - можно ли отвечать на комментарий
  const canReplyToComment = (comment: Comment) => {
    // Если есть child с данными - нельзя отвечать
    if (comment.child && comment.child.uuid && comment.child.body) {
      return false;
    }
    // Во всех остальных случаях можно отвечать
    return true;
  };

  // Проверка возможности отправки
  const canSubmitComment = Boolean(
    wellUuid && 
    newComment?.trim() && 
    getUserUuid()
  );

  const canSubmitReply = Boolean(
    wellUuid && 
    replyText?.trim() && 
    getUserUuid()
  );

  return (
    <div className={classes.comment}>
      <Text weight='bold' size='xl'>Комментарии и вопросы</Text>
      
      {isLoading && (
        <Text>Загрузка комментариев...</Text>
      )}
      
      {error && (
        <Text view="alert">{error}</Text>
      )}
      
      <div className={classes.commentCards}>
        {!isLoading && !error && comments.length === 0 && (
          <Text view="secondary">Комментариев пока нет</Text>
        )}
        
        {comments.map((comment) => (
          <div key={comment.uuid}>
            <CommentCard 
              author={comment.user_fio || getUserFullName()}
              date={formatCommentDate(comment.created_at)}
              text={comment.body}
              onReply={() => handleStartReply(comment.uuid)}
              canReply={canReplyToComment(comment)}
            />
            
            {/* Форма ответа на комментарий */}
            {replyingTo === comment.uuid && (
              <Card verticalSpace="m" horizontalSpace="m" style={{ marginTop: '10px', marginLeft: '20px' }}>
                <TextField
                  type="textarea"
                  placeholder="Введите ваш ответ..."
                  value={replyText}
                  onChange={setReplyText}
                  onKeyPress={(e) => handleReplyKeyPress(e, comment.uuid)}
                  rows={3}
                />
                <div style={{ display: 'flex', gap: '10px', marginTop: '10px', justifyContent: 'flex-end' }}>
                  <Button 
                    size="xs" 
                    label="Отмена" 
                    onClick={handleCancelReply}
                  />
                  <Button 
                    size="xs" 
                    label="Ответить" 
                    onClick={() => handleReply(comment.uuid)}
                    disabled={!canSubmitReply}
                  />
                </div>
              </Card>
            )}
            
            {/* Отображение дочерних комментариев (ответов) - только если они реально существуют */}
            {comment.child && comment.child.uuid && comment.child.body && (
              <div style={{ marginLeft: '20px', marginTop: '10px' }}>
                <CommentCard 
                  author={comment.child.user_fio || getUserFullName()}
                  date={formatCommentDate(comment.child.created_at)}
                  text={comment.child.body}
                  onReply={() => {}}
                  canReply={false}
                  isChild={true}
                />
              </div>
            )}
          </div>
        ))}
        
        <AddCommentCard 
          value={newComment}
          onChange={setNewComment}
          onAdd={handleAddComment}
          onKeyPress={handleKeyPress}
          disabled={!canSubmitComment}
        />
      </div>
    </div>
  );
};

interface CommentCardProps {
    author: string;
    date: string;
    text: string;
    onReply: () => void;
    canReply: boolean;
    isChild?: boolean;
}

const CommentCard = ({ author, date, text, onReply, canReply, isChild = false }: CommentCardProps) => (
    <Card 
        verticalSpace="l" 
        horizontalSpace="l" 
        className={classes.cardContainerOptions}
        style={isChild ? { backgroundColor: 'rgba(0, 0, 0, 0.02)', borderLeft: '3px solid #0078D2' } : {}}
    >
        <Text size='s' style={{ display: 'flex', flexDirection: 'row' }}>
            {author} - <Text size='s' view='secondary'> {date}</Text>
        </Text>
        <Text size='s'>{text}</Text>
        {canReply && (
            <div style={{ display: 'flex', justifyContent: 'end' }}>
                <Button 
                    label='Ответить на комментарий' 
                    size='xs' 
                    onClick={onReply}
                />
            </div>
        )}
    </Card>
);

interface AddCommentCardProps {
    value: string;
    onChange: (value: any) => void;
    onAdd: () => void;
    onKeyPress: (e: React.KeyboardEvent) => void;
    isLoading?: boolean;
    disabled: boolean;
}

const AddCommentCard = ({ value, onChange, onAdd, onKeyPress, isLoading, disabled }: AddCommentCardProps) => (
    <Card className={classes.addComment}>
        <TextField
            type="textarea"
            label='Оставить комментарий'
            placeholder='Введите ваш комментарий'
            value={value}
            onChange={onChange}
            onKeyPress={onKeyPress}
            disabled={isLoading}
            rows={3}
        />
        <Button 
            style={{ width: '200px' }} 
            label={isLoading ? 'Отправка...' : 'Отправить'} 
            onClick={onAdd}
            disabled={disabled || isLoading}
            loading={isLoading}
        />
    </Card>
);

// Компонент модалки редактирования графика
const EditChartModal = ({
    isOpen,
    onClose,
    chartUuid,
    onChartUpdated
}: {
    isOpen: boolean;
    onClose: () => void;
    chartUuid: string;
    onChartUpdated: () => void;
}) => {
    const [triggerGetChartUuid, { data: chartData, isLoading: isLoadingChart }] = useLazyGetChartUuidQuery();
    const [updateChart, { isLoading: isUpdating }] = useUpdateChartMutation();

    const [name, setName] = useState('');
    const [comment, setComment] = useState('');
    const [error, setError] = useState('');

    useEffect(() => {
        if (isOpen && chartUuid) {
            triggerGetChartUuid({ uuid: chartUuid })
                .unwrap()
                .then((data) => {
                    setName(data.name || '');
                    setComment(data.comment || '');
                })
                .catch((err) => {
                    console.error('Ошибка загрузки данных графика:', err);
                    setError('Не удалось загрузить данные графика');
                });
        }
    }, [isOpen, chartUuid, triggerGetChartUuid]);

    const handleSave = async () => {
        if (!name.trim()) {
            setError('Название графика обязательно');
            return;
        }

        setError('');

        try {
            await updateChart({
                uuid: chartUuid,
                comment: comment,
                build_by_parameter: chartData?.build_by_parameter || '',
                name: name
            }).unwrap();

            onChartUpdated();
            onClose();
        } catch (err) {
            console.error('Ошибка обновления графика:', err);
            setError('Не удалось обновить график');
        }
    };

    const handleClose = () => {
        setName('');
        setComment('');
        setError('');
        onClose();
    };

    return (
        <Modal isOpen={isOpen} hasOverlay onClickOutside={handleClose} onEsc={handleClose}>
            <div className={classes.editModal}>
                <Text size="xl" weight="bold" className={classes.modalTitle}>
                    Редактировать график
                </Text>

                {(isLoadingChart || isUpdating) && <Text>Загрузка...</Text>}

                {error && (
                    <Text view="alert" size="s">
                        {error}
                    </Text>
                )}

                {!isLoadingChart && (
                    <>
                        <TextField
                            label="Название графика"
                            value={name}
                            onChange={(value) => setName(value || '')}
                            placeholder="Введите название графика"
                            required
                        />

                        <TextField
                            label="Описание"
                            value={comment}
                            onChange={(value) => setComment(value || '')}
                            placeholder="Введите описание графика"
                            type="textarea"
                            rows={3}
                        />

                        <div className={classes.modalButtons}>
                            <Button
                                label="Отмена"
                                view="secondary"
                                onClick={handleClose}
                                disabled={isUpdating}
                            />
                            <Button
                                label={isUpdating ? "Сохранение..." : "Сохранить"}
                                onClick={handleSave}
                                disabled={isUpdating || !name.trim()}
                                loading={isUpdating}
                            />
                        </div>
                    </>
                )}
            </div>
        </Modal>
    );
};

// Компонент модалки редактирования карты интерполяции
const EditMapModal = ({
    isOpen,
    onClose,
    mapData,
    onMapUpdated
}: {
    isOpen: boolean;
    onClose: () => void;
    mapData: InterpolationMap | null;
    onMapUpdated: () => void;
}) => {
    const [patchInterpolationMap, { isLoading: isUpdating }] = usePatchInterpolationMapMutation();

    const [name, setName] = useState('');
    const [comment, setComment] = useState('');
    const [error, setError] = useState('');

    useEffect(() => {
        if (isOpen && mapData) {
            setName(mapData.name || '');
            setComment(mapData.comment || '');
        }
    }, [isOpen, mapData]);

    const handleSave = async () => {
        if (!name.trim()) {
            setError('Название карты обязательно');
            return;
        }

        if (!mapData) {
            setError('Ошибка: данные карты не загружены');
            return;
        }

        setError('');

        try {
            await patchInterpolationMap({
                uuid: mapData.uuid,
                name: name,
                comment: comment,
                well: mapData.well
            }).unwrap();

            onMapUpdated();
            onClose();
        } catch (err) {
            console.error('Ошибка обновления карты:', err);
            setError('Не удалось обновить карту');
        }
    };

    const handleClose = () => {
        setName('');
        setComment('');
        setError('');
        onClose();
    };

    return (
        <Modal isOpen={isOpen} hasOverlay onClickOutside={handleClose} onEsc={handleClose}>
            <div className={classes.editModal}>
                <Text size="xl" weight="bold" className={classes.modalTitle}>
                    Редактировать карту интерполяции
                </Text>

                {error && (
                    <Text view="alert" size="s">
                        {error}
                    </Text>
                )}

                <TextField
                    label="Название карты"
                    value={name}
                    onChange={(value) => setName(value || '')}
                    placeholder="Введите название карты"
                    required
                />

                <TextField
                    label="Описание"
                    value={comment}
                    onChange={(value) => setComment(value || '')}
                    placeholder="Введите описание карты"
                    type="textarea"
                    rows={3}
                />

                <div className={classes.modalButtons}>
                    <Button
                        label="Отмена"
                        view="secondary"
                        onClick={handleClose}
                        disabled={isUpdating}
                    />
                    <Button
                        label={isUpdating ? "Сохранение..." : "Сохранить"}
                        onClick={handleSave}
                        disabled={isUpdating || !name.trim()}
                        loading={isUpdating}
                    />
                </div>
            </div>
        </Modal>
    );
};

// Компонент модалки редактирования заключения
const EditConclusionModal = ({
    isOpen,
    onClose,
    wellUuid,
    currentResult,
    onConclusionUpdated
}: {
    isOpen: boolean;
    onClose: () => void;
    wellUuid: string;
    currentResult: string;
    onConclusionUpdated: () => void;
}) => {
    const [patchWell, { isLoading: isUpdating }] = usePatchWellMutation();

    const [result, setResult] = useState('');
    const [error, setError] = useState('');

    useEffect(() => {
        if (isOpen) {
            setResult(currentResult || '');
        }
    }, [isOpen, currentResult]);

    const handleSave = async () => {
        setError('');

        try {
            await patchWell({
                uuid: wellUuid,
                result: result
            }).unwrap();

            onConclusionUpdated();
            onClose();
        } catch (err) {
            console.error('Ошибка обновления заключения:', err);
            setError('Не удалось обновить заключение');
        }
    };

    const handleClose = () => {
        setResult('');
        setError('');
        onClose();
    };

    return (
        <Modal isOpen={isOpen} hasOverlay onClickOutside={handleClose} onEsc={handleClose}>
            <div className={classes.editModal} style={{ display: 'flex', flexDirection: 'column', padding: '20px', gap: '20px', minWidth: '500px' }}>
                <Text size="xl" weight="bold" className={classes.modalTitle}>
                    Редактировать заключение
                </Text>

                {error && (
                    <Text view="alert" size="s">
                        {error}
                    </Text>
                )}

                <TextField
                    label="Заключение"
                    value={result}
                    onChange={(value) => setResult(value || '')}
                    placeholder="Введите текст заключения"
                    type="textarea"
                    rows={6}
                />

                <div className={classes.modalButtons} style={{ display: 'flex', justifyContent: 'end', gap: '20px' }}>
                    <Button
                        label="Отмена"
                        view="secondary"
                        onClick={handleClose}
                        disabled={isUpdating}
                    />
                    <Button
                        label={isUpdating ? "Сохранение..." : "Сохранить"}
                        onClick={handleSave}
                        disabled={isUpdating}
                        loading={isUpdating}
                    />
                </div>
            </div>
        </Modal>
    );
};

// Основной компонент ProjectResults
const ProjectResults = ({ userAccessLevel, projectData, projectUuid }: ProjectResultsProps) => {
    const [wells, setWells] = useState<WellItem[]>([]);
    const [selectedWell, setSelectedWell] = useState<WellItem | null>(null);
    const location = useLocation();
    
    const [triggerGetChartByWell, { data: chartsData, isLoading: isLoadingCharts, error: chartsError }] = useLazyGetChartByWellQuery();
    const [triggerGetWellUuid, { data: wellData, isLoading: isLoadingWell }] = useLazyGetWellUuidQuery();
    const [triggerGetWellByProject, { data: wellsData, isLoading: isLoadingWells }] = useLazyGetWellByProjectQuery();
    const [triggerGetWellInterpolationMaps, { data: interpolationMapsData, isLoading: isLoadingMaps }] = useLazyGetWellInterpolationMapsQuery();
    const [exportWellResultsReport, { isLoading: isExporting }] = useExportWellResultsReportMutation();

    const [resultTables, setResultTables] = useState<ResultTable[]>([]);
    const [editingTableId, setEditingTableId] = useState<string | null>(null);

    const [isAddGraphicModalOpen, setIsAddGraphicModalOpen] = useState(false);
    const [isAddMapModalOpen, setIsAddMapModalOpen] = useState(false);
    const [isEditConclusionModalOpen, setIsEditConclusionModalOpen] = useState(false);
    const [editingChartUuid, setEditingChartUuid] = useState<string | null>(null);
    const [editingMap, setEditingMap] = useState<InterpolationMap | null>(null);

    const [triggerGetResultTables] = useLazyGetResultTableQuery();
    const [postResultTable] = usePostResultTableMutation();
    const [patchResultTable] = usePatchResultTableMutation();
    const [deleteResultTable] = useDeleteResultTableMutation();

    // Загрузка скважин проекта
    useEffect(() => {
    if (projectUuid) {
        triggerGetWellByProject({ project_uuid: projectUuid, category: 'Исследуемая', limit: 9999 })
            .unwrap()
            .then((data) => {
                //(data)
                if (data?.results) {
                    const wellsList: WellItem[] = data.results.map((well: any) => ({
                        id: well.uuid,
                        label: `Скважина ${well.number}`,
                        uuid: well.uuid
                    }));
                    setWells(wellsList);
                    
                    // Проверяем, есть ли выбранная скважина в состоянии навигации
                    const locationState = location.state as { selectedWellUuid?: string };
                    const selectedWellUuid = locationState?.selectedWellUuid;
                    
                    if (selectedWellUuid) {
                        const foundWell = wellsList.find(well => well.uuid === selectedWellUuid);
                        if (foundWell) {
                            setSelectedWell(foundWell);
                        } else if (wellsList.length > 0) {
                            setSelectedWell(wellsList[0]);
                        }
                    } else if (wellsList.length > 0) {
                        setSelectedWell(wellsList[0]);
                    }
                }
                 })
            .catch(console.error);
    }
}, [projectUuid, triggerGetWellByProject, location.state]);

    // Загрузка данных при изменении выбранной скважины
    useEffect(() => {
        if (selectedWell) {
            triggerGetChartByWell({ well_uuid: selectedWell.uuid });
            triggerGetWellUuid({ uuid: selectedWell.uuid });
            triggerGetWellInterpolationMaps({ uuid: selectedWell.uuid });
            loadResultTables(selectedWell.uuid);
        }
    }, [selectedWell]);

    const loadResultTables = async (wellUuid: string) => {
        try {
            const result = await triggerGetResultTables({
                well: wellUuid,
                limit: 100,
                offset: 0,
                ordering: '-created_at'
            }).unwrap();
            
            const normalizedTables = (result.results || []).map((table: any) => 
                TableUtils.normalizeTableData(table)
            );
            
            setResultTables(normalizedTables);
        } catch (error) {
            console.error('Ошибка загрузки таблиц:', error);
            setResultTables([]);
        }
    };

    // Создание новой таблицы
    const handleCreateTable = async () => {
        if (!selectedWell) return;

        const defaultTableData = TableUtils.createDefaultTableData();
        
        const newTable = {
            name: `Таблица результатов ${new Date().toLocaleDateString()}`,
            content: JSON.stringify(defaultTableData),
            well: selectedWell.uuid
        };

        try {
            const result = await postResultTable(newTable).unwrap();
            const normalizedTable = TableUtils.normalizeTableData(result);
            
            setResultTables(prev => [normalizedTable, ...prev]);
            setEditingTableId(normalizedTable.uuid);
        } catch (error) {
            console.error('Ошибка создания таблицы:', error);
            alert('Ошибка при создании таблицы');
        }
    };

    // Обновление таблицы
    const handleTableUpdate = async (updatedTable: ResultTable) => {
        try {
            const result = await patchResultTable({
                uuid: updatedTable.uuid,
                name: updatedTable.name,
                content: JSON.stringify(updatedTable.content),
                well: updatedTable.well
            }).unwrap();

            const normalizedTable = TableUtils.normalizeTableData(result);

            setResultTables(prev => 
                prev.map(table => table.uuid === updatedTable.uuid ? normalizedTable : table)
            );
        } catch (error) {
            console.error('Ошибка обновления таблицы:', error);
            throw error;
        }
    };

    // Удаление таблицы
    const handleTableDelete = async (tableUuid: string) => {
        try {
            await deleteResultTable({ uuid: tableUuid }).unwrap();
            setResultTables(prev => prev.filter(table => table.uuid !== tableUuid));
            if (editingTableId === tableUuid) {
                setEditingTableId(null);
            }
        } catch (error) {
            console.error('Ошибка удаления таблицы:', error);
            alert('Ошибка при удалении таблицы');
        }
    };

    const handleComboboxChange = (value: WellItem | null) => {
        setSelectedWell(value);
    };

    const handleChartCreated = () => {
        if (selectedWell) {
            triggerGetChartByWell({ well_uuid: selectedWell.uuid });
        }
        setIsAddGraphicModalOpen(false);
    };

    const handleMapCreated = () => {
        if (selectedWell) {
            triggerGetWellInterpolationMaps({ uuid: selectedWell.uuid });
        }
        setIsAddMapModalOpen(false);
    };

    const handleEditChart = (chartUuid: string) => {
        setEditingChartUuid(chartUuid);
    };

    const handleEditMap = (map: InterpolationMap) => {
        setEditingMap(map);
    };

    const handleChartUpdated = () => {
        if (selectedWell) {
            triggerGetChartByWell({ well_uuid: selectedWell.uuid });
        }
        setEditingChartUuid(null);
    };

    const handleMapUpdated = () => {
        if (selectedWell) {
            triggerGetWellInterpolationMaps({ uuid: selectedWell.uuid });
        }
        setEditingMap(null);
    };

    const handleEditConclusion = () => {
        setIsEditConclusionModalOpen(true);
    };

    const handleConclusionUpdated = () => {
        if (selectedWell) {
            triggerGetWellUuid({ uuid: selectedWell.uuid });
        }
        setIsEditConclusionModalOpen(false);
    };
    
    const [getReportWellInfo, {data: wellInfo}] = useLazyGetReportWellInfoQuery()

    useEffect(() => {
            if (selectedWell) {
            getReportWellInfo({ uuid: selectedWell.uuid });
        }
    },[selectedWell])

    const handleExportReport = async () => {
        if (!selectedWell?.uuid) return;
        try {
            await exportWellResultsReport({ 
                well_uuid: selectedWell.uuid,
                name: wellInfo?.full_name 
            }).unwrap();
            //('📄 Отчет экспортирован');
        } catch (err) {
            console.error('Ошибка экспорта отчета:', err);
            alert('Ошибка при экспорте отчета');
        }
    };

    const canEdit = userAccessLevel !== null && userAccessLevel !== 0;

    return (
        <div className={classes.main}>
            {/* Заголовок и выбор скважины */}
            <div className={classes.header}>
                <div>
                    <Text size="2xl" weight="bold">
                        Результаты и отчетные материалы. Проект {projectData?.number ? `№ ${projectData.number}` : projectUuid}
                    </Text>
                    <Text size="s" view="secondary">
                        Графики, карты, таблицы и заключения по проекту
                    </Text>
                </div>
                <div className={classes.headerControls}>
                    <Text>Исследуемая скважина</Text>
                    <Combobox
                        items={wells}
                        value={selectedWell}
                        onChange={handleComboboxChange}
                        placeholder={isLoadingWells ? "Загрузка..." : "Выберите скважину"}
                        disabled={isLoadingWells}
                        style={{ width: '250px' }}
                    />
                </div>
            </div>

            {/* Кнопки действий */}
            {canEdit && selectedWell && (
                <div className={classes.buttons}>
                    <Button
                        label="Добавить график"
                        iconLeft={IconAreaChart}
                        onClick={() => setIsAddGraphicModalOpen(true)}
                    />
                    <Button
                        view="secondary"
                        label="Добавить карту интерполяции"
                        iconLeft={IconGas}
                        onClick={() => setIsAddMapModalOpen(true)}
                    />
                </div>
            )}

            <div className={classes.container}>
                {/* Секция графиков */}
                <GraphicsSection
                    userAccessLevel={userAccessLevel}
                    chartsData={chartsData}
                    isLoading={isLoadingCharts}
                    error={chartsError}
                    onEditChart={handleEditChart}
                />

                {/* Секция карт интерполяции */}
                <InterpolationMapsSection
                    userAccessLevel={userAccessLevel}
                    interpolationMapsData={interpolationMapsData}
                    isLoading={isLoadingMaps}
                    onEditMap={handleEditMap}
                />

                {/* Секция таблиц результатов */}
                <div className={classes.tablesSection}>
                    <div className={classes.sectionHeader}>
                        <Text size="xl" weight="bold">
                            Таблицы результатов
                        </Text>
                        {canEdit && (
                            <Button
                                label="Добавить таблицу"
                                onClick={handleCreateTable}
                                iconLeft={IconAdd}
                            />
                        )}
                    </div>

                    {resultTables.length === 0 ? (
                        <Card className={classes.emptyState}>
                            <Text>Нет созданных таблиц</Text>
                            {canEdit && (
                                <Text size="s" view="secondary">
                                    Нажмите "Добавить таблицу" чтобы создать первую таблицу
                                </Text>
                            )}
                        </Card>
                    ) : (
                        <div className={classes.tablesList}>
                            {resultTables.map((table) => (
                                <div key={table.uuid} className={classes.tableItem}>
                                    <CustomTable
                                        table={table}
                                        isEditing={editingTableId === table.uuid}
                                        onTableUpdate={handleTableUpdate}
                                        onTableDelete={handleTableDelete}
                                        userAccessLevel={userAccessLevel}
                                    />
                                    
                                    {canEdit && (
                                        <div className={classes.tableActionsBottom}>
                                            <Button
                                                size="s"
                                                view={editingTableId === table.uuid ? "primary" : "secondary"}
                                                label={editingTableId === table.uuid ? "Завершить редактирование" : "Редактировать таблицу"}
                                                iconLeft={IconEdit}
                                                onClick={() => setEditingTableId(
                                                    editingTableId === table.uuid ? null : table.uuid
                                                )}
                                            />
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Секция заключения */}
                <ConclusionSection
                    userAccessLevel={userAccessLevel}
                    wellData={wellData}
                    isLoadingWell={isLoadingWell}
                    onEditConclusion={handleEditConclusion}
                    onExportReport={handleExportReport}
                    isExporting={isExporting}
                />

                {/* Секция комментариев */}
                <CommentsSection wellUuid={selectedWell?.uuid} />
            </div>

            {/* Модальные окна */}
            <AddGraphic
                isOpen={isAddGraphicModalOpen}
                handleCloseModal={() => setIsAddGraphicModalOpen(false)}
                onChartCreated={handleChartCreated}
                wellUuid={selectedWell?.uuid || ''}
            />
            <AddMap
                isOpen={isAddMapModalOpen}
                handleCloseModal={() => setIsAddMapModalOpen(false)}
                onMapCreated={handleMapCreated}
                wellUuid={selectedWell?.uuid || ''}
            />

            <EditChartModal
                isOpen={!!editingChartUuid}
                onClose={() => setEditingChartUuid(null)}
                chartUuid={editingChartUuid || ''}
                onChartUpdated={handleChartUpdated}
            />

            <EditMapModal
                isOpen={!!editingMap}
                onClose={() => setEditingMap(null)}
                mapData={editingMap}
                onMapUpdated={handleMapUpdated}
            />

            <EditConclusionModal
                isOpen={isEditConclusionModalOpen}
                onClose={() => setIsEditConclusionModalOpen(false)}
                wellUuid={selectedWell?.uuid || ''}
                currentResult={wellData?.result || ''}
                onConclusionUpdated={handleConclusionUpdated}
            />
        </div>
    );
};

export default ProjectResults;